import type { MedicationPadItemType } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import type { ProductFormulation } from '@/constants/products';
import { ProductFormulations } from '@/constants/products';
import { formatDispenseAmountWithUnit } from './formatDispenseAmountWithUnit';
import type { DispenseLimitRow } from './getFormulationDispenseLimitRows';
import { getFormulationFilter } from './getFormulationFilter';
import { getFormulationUnit } from './getFormulationUnit';

/**
 * The words around the formulation's name, shared by the phrase and the pattern that strips it. The name is the pad
 * item's Select Formulation value, as `Total for all Herb, Dried` reads it, not the formulation's display name
 */
const monthlyDispenseLimitLabel = (formulationName: string) => `Monthly ${formulationName} dispense limit`;

/**
 * The monthly dispense limit phrase, e.g. 'Monthly Herb, Dried dispense limit 64.4 g'; undefined without a limit or a
 * formulation. Whether the item gets a phrase at all is the caller's decision.
 */
export const getMonthlyDispenseLimitPhrase = (
  limit: number | undefined,
  formulation: ProductFormulation | undefined,
) => {
  if (limit === undefined || formulation === undefined) {
    return undefined;
  }
  const amount = formatDispenseAmountWithUnit(limit, getFormulationUnit(formulation));
  return `${monthlyDispenseLimitLabel(formulation)} ${amount}`;
};

const FORMULATION_NAMES = Object.values(ProductFormulations).map((formulation) =>
  formulation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
);

// Only what the phrase above writes, as the final clause after one of `appendDosageSuffix`'s own separators ("; ",
// or a space after punctuation) or as the whole text, and case-sensitive, so a "monthly" in the prescriber's own last
// clause, or the phrase typed straight after their words, is left alone. The figure is matched rather than known,
// since a reissued script carries the limit it was written with
const MONTHLY_DISPENSE_LIMIT_CLAUSE = new RegExp(
  `(?:^|; |(?<=[.,;:!?]) )${monthlyDispenseLimitLabel(`(?:${FORMULATION_NAMES.join('|')})`)} \\d+(?:\\.\\d+)?(?: ?[A-Za-z]+)?\\s*$`,
);

/** Dosage text without the monthly dispense limit phrase it ends with; text without one is returned as it is. */
export const removeMonthlyDispenseLimitPhrase = (dosage: string) =>
  MONTHLY_DISPENSE_LIMIT_CLAUSE.test(dosage) ? dosage.replace(MONTHLY_DISPENSE_LIMIT_CLAUSE, '').trimEnd() : dosage;

/**
 * `getMonthlyDispenseLimitPhrase` for a pad item with its formulation's limit in force, read from the row (the bar is
 * the source of truth for the figure shared with the pharmacy); undefined without a row.
 * @param dispenseLimitRows - every formulation's row, computed once for the pad rather than once per item
 */
export const getPadItemMonthlyDispenseLimitPhrase = (
  item: MedicationPadItemType,
  dispenseLimitRows: DispenseLimitRow[],
) => {
  const formulation = getFormulationFilter(item.filters);
  return getMonthlyDispenseLimitPhrase(
    dispenseLimitRows.find((row) => row.formulation === formulation)?.limit,
    formulation,
  );
};
